import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import productService from '../../services/productService';

// -------------------- Initial State --------------------
const initialState = {
  products: [],
  product: null,
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: '',
  productLoading: false,
  productError: null,
  totalPages: 0,
  currentPage: 1,
  filters: {
    search: '',
    category: '',
    priceRange: [0, 10000],
    moq: '',
    inStock: false,
  },
  selectedIds: [],
};

// Helper to extract message
const extractMessage = (error) => (
  (error.response && error.response.data && error.response.data.message) ||
  error.message ||
  error.toString()
);

// -------------------- Thunks --------------------
export const getProducts = createAsyncThunk('products/getAll', async (filters, thunkAPI) => {
  try {
    return await productService.getProducts(filters);
  } catch (error) {
    return thunkAPI.rejectWithValue(extractMessage(error));
  }
});

export const getMyProducts = createAsyncThunk('products/getMine', async (_, thunkAPI) => {
  try {
    return await productService.getMyProducts();
  } catch (error) {
    return thunkAPI.rejectWithValue(extractMessage(error));
  }
});

export const createProduct = createAsyncThunk('products/create', async (productData, thunkAPI) => {
  try {
    return await productService.createProduct(productData);
  } catch (error) {
    return thunkAPI.rejectWithValue(extractMessage(error));
  }
});

export const updateProduct = createAsyncThunk('products/update', async ({ id, productData }, thunkAPI) => {
  try {
    return await productService.updateProduct(id, productData);
  } catch (error) {
    return thunkAPI.rejectWithValue(extractMessage(error));
  }
});

export const deleteProduct = createAsyncThunk('products/delete', async (id, thunkAPI) => {
  try {
    await productService.deleteProduct(id);
    return { id };
  } catch (error) {
    return thunkAPI.rejectWithValue(extractMessage(error));
  }
});

export const toggleActivation = createAsyncThunk('products/toggleActivation', async (id, thunkAPI) => {
  try {
    return await productService.toggleProductActivation(id); // returns { id, isActive }
  } catch (error) {
    return thunkAPI.rejectWithValue(extractMessage(error));
  }
});

// Get single product (detail view)
export const getProductById = createAsyncThunk('products/getOne', async (id, thunkAPI) => {
  try {
    return await productService.getProduct(id);
  } catch (error) {
    return thunkAPI.rejectWithValue(extractMessage(error));
  }
});

export const bulkToggleActivation = createAsyncThunk('products/bulkToggle', async ({ ids }, thunkAPI) => {
  try {
    const results = [];
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      const r = await productService.toggleProductActivation(id);
      results.push(r);
    }
    return results; // [{id,isActive},...]
  } catch (error) {
    return thunkAPI.rejectWithValue(extractMessage(error));
  }
});

// -------------------- Slice --------------------
export const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = '';
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearProduct: (state) => {
      state.product = null;
      state.productLoading = false;
      state.productError = null;
    },
    toggleSelected: (state, action) => {
      const id = action.payload;
      state.selectedIds = state.selectedIds.includes(id)
        ? state.selectedIds.filter(x => x !== id)
        : [...state.selectedIds, id];
    },
    clearSelection: (state) => {
      state.selectedIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Get products (retailer browse)
      .addCase(getProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.products = action.payload.products;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(getProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Get my products (wholesaler)
      .addCase(getMyProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getMyProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.products = action.payload;
      })
      .addCase(getMyProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Create
      .addCase(createProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.products.push(action.payload);
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Update
      .addCase(updateProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.products = state.products.map(p => p._id === action.payload._id ? action.payload : p);
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Delete
      .addCase(deleteProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.products = state.products.filter(p => p._id !== action.payload.id);
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Single activation toggle
      .addCase(toggleActivation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(toggleActivation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.products = state.products.map(p => p._id === action.payload.id ? { ...p, isActive: action.payload.isActive } : p);
      })
      .addCase(toggleActivation.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Bulk activation toggle
      .addCase(bulkToggleActivation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(bulkToggleActivation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const map = Object.fromEntries(action.payload.map(r => [r.id, r.isActive]));
        state.products = state.products.map(p => (map[p._id] !== undefined ? { ...p, isActive: map[p._id] } : p));
        state.selectedIds = [];
      })
      .addCase(bulkToggleActivation.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Single product fetch
      .addCase(getProductById.pending, (state) => {
        state.productLoading = true;
        state.productError = null;
      })
      .addCase(getProductById.fulfilled, (state, action) => {
        state.productLoading = false;
        state.product = action.payload;
      })
      .addCase(getProductById.rejected, (state, action) => {
        state.productLoading = false;
        state.productError = action.payload;
      });
  },
});

export const { reset, setFilters, clearProduct, toggleSelected, clearSelection } = productSlice.actions;
export default productSlice.reducer;